/**
 * Platform layer: lets the shared UI tell the browser from the Tauri desktop
 * app without importing Tauri eagerly. `@tauri-apps/api` is only loaded
 * (dynamically) once `isDesktop()` is true, so the web bundle never runs it.
 */

/** True inside the Tauri (v2) webview. */
export function isDesktop(): boolean {
  return typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
}

export type Unlisten = () => void;

export interface TauriBridge {
  /** A `Uint8Array` is sent as a raw request body, for media too frequent for JSON. */
  invoke<T = void>(command: string, args?: Record<string, unknown> | Uint8Array): Promise<T>;
  listen<T>(event: string, handler: (payload: T) => void): Promise<Unlisten>;
}

let bridge: Promise<TauriBridge> | null = null;

/** The `invoke`/`listen` pair of the desktop shell. Only call when `isDesktop()`. */
export function tauri(): Promise<TauriBridge> {
  bridge ??= Promise.all([import("@tauri-apps/api/core"), import("@tauri-apps/api/event")]).then(([core, event]) => ({
    invoke: <T = void>(command: string, args?: Record<string, unknown> | Uint8Array) => core.invoke<T>(command, args),
    listen: <T>(name: string, handler: (payload: T) => void) => event.listen<T>(name, (e) => handler(e.payload)),
  }));
  return bridge;
}

/** True where the OS has a microphone privacy page we can open (desktop app on macOS or Windows). */
export function canOpenMicrophoneSettings(): boolean {
  return isDesktop() && /Mac|Windows/i.test(navigator.userAgent);
}

/** Opens the OS microphone privacy settings (desktop app only). */
export async function openMicrophoneSettings(): Promise<void> {
  await (await tauri()).invoke("open_mic_privacy_settings");
}

/** Opens the screen recording privacy page in the desktop app. */
export async function openScreenRecordingSettings(): Promise<void> {
  await (await tauri()).invoke("open_screen_privacy_settings");
}

/** WebView2 refusal is controlled by its picker/policy; a Windows OS shortcut cannot fix it. */
export function canOpenScreenRecordingSettings(): boolean {
  return isDesktop() && !/iPhone|iPad/.test(navigator.userAgent) && /Macintosh|Mac OS X/.test(navigator.userAgent);
}
